import React, { useMemo, useState } from "react";
import { useApp } from "@/context/AppContext";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { fishReaction, SPECIES, EMOJIS, COLORS } from "@/lib/helpers";
import {
  Flag,
  ArrowLeft,
  Trash2,
  Pencil,
  Save,
  X,
  Minus,
  Plus as PlusIcon,
} from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";

export default function CatchDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { members, catches, activeUser, refresh, setCatches } = useApp();

  const memberById = useMemo(
    () => Object.fromEntries(members.map((m) => [m.id, m])),
    [members],
  );
  const c = catches.find((x) => x.id === id);

  const [showBulldust, setShowBulldust] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [editLength, setEditLength] = useState(c?.length_cm || 25);
  const [editSpecies, setEditSpecies] = useState(c?.species || "Other");
  const [editOwner, setEditOwner] = useState(c?.catch_owner_id || "");

  if (!c) {
    return (
      <div className="p-8 text-center">
        <p className="text-[#556B7D] mb-4">Catch not found. Weird.</p>
        <button
          onClick={() => navigate("/feed")}
          className="text-[#E8A838] font-bold"
          data-testid="detail-back-btn"
        >
          Back to feed
        </button>
      </div>
    );
  }

  const owner = memberById[c.catch_owner_id];
  const enterer = memberById[c.entered_by_user_id];
  const reaction = fishReaction(c.length_cm);
  const flags = c.bulldust_flagged_by || [];
  const iFlagged = activeUser && flags.includes(activeUser.id);
  const loggedByOther = c.entered_by_user_id !== c.catch_owner_id;

  const toggleBulldust = async () => {
    if (!activeUser) return;
    const updated = await api.toggleBulldust(c.id, activeUser.id);
    setCatches((prev) => prev.map((x) => (x.id === c.id ? updated : x)));
    setShowBulldust(false);
    if (updated.bulldust_flagged_by.includes(activeUser.id)) {
      toast(`🚩 Flagged. Family will know.`);
    } else {
      toast(`Flag removed. Peace restored.`);
    }
  };

  const doDelete = async () => {
    await api.deleteCatch(c.id);
    setShowDelete(false);
    toast(`Gone. ${owner?.name || "That angler"}'s ${c.length_cm.toFixed(0)}cm wiped from history.`);
    navigate("/feed");
    await refresh();
  };

  const openEdit = () => {
    setEditLength(c.length_cm);
    setEditSpecies(c.species || "Other");
    setEditOwner(c.catch_owner_id);
    setShowEdit(true);
  };

  const saveEdit = async () => {
    const updated = await api.updateCatch(c.id, {
      length_cm: editLength,
      species: editSpecies,
      catch_owner_id: editOwner,
    });
    setCatches((prev) => prev.map((x) => (x.id === c.id ? updated : x)));
    setShowEdit(false);
    toast("Fixed. History rewritten.");
  };

  return (
    <div className="pb-8">
      {/* Header */}
      <div className="flex items-center justify-between px-4 pt-3 pb-2">
        <button
          onClick={() => navigate(-1)}
          className="w-11 h-11 rounded-full bg-white shadow-sm flex items-center justify-center active:scale-95 border border-[#0A3D62]/10"
          data-testid="detail-back-btn"
        >
          <ArrowLeft className="w-5 h-5 text-[#0A3D62]" strokeWidth={2.5} />
        </button>
        <div className="flex gap-2">
          <button
            onClick={openEdit}
            className="w-11 h-11 rounded-full bg-white shadow-sm flex items-center justify-center active:scale-95 border border-[#0A3D62]/10"
            data-testid="detail-edit-btn"
          >
            <Pencil className="w-4 h-4 text-[#0A3D62]" strokeWidth={2.5} />
          </button>
          <button
            onClick={() => setShowDelete(true)}
            className="w-11 h-11 rounded-full bg-[#D9381E]/10 flex items-center justify-center active:scale-95"
            data-testid="detail-delete-btn"
          >
            <Trash2 className="w-4 h-4 text-[#D9381E]" strokeWidth={2.5} />
          </button>
        </div>
      </div>

      {/* Photo */}
      <div className="aspect-square bg-[#0A3D62]/5 mx-4 rounded-3xl overflow-hidden relative">
        {c.photo_base64 ? (
          <img src={c.photo_base64} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-8xl">🐟</div>
        )}
        <div
          className="absolute bottom-4 left-4 font-display text-4xl font-black px-3 py-1 rounded-xl"
          style={{ background: reaction.color, color: "#FFF" }}
          data-testid="detail-length"
        >
          {c.length_cm.toFixed(0)}
          <span className="text-lg ml-1">cm</span>
        </div>
        <div
          className="absolute bottom-4 right-4 text-sm font-bold text-white px-3 py-1.5 rounded-lg bg-black/40 backdrop-blur"
          data-testid="detail-reaction"
        >
          {reaction.label}
        </div>
      </div>

      {/* Info */}
      <div className="px-5 mt-5 space-y-4">
        {owner && (
          <div className="flex items-center gap-3 bg-white rounded-2xl p-4 border border-[#0A3D62]/5">
            <div
              className="w-14 h-14 rounded-full flex items-center justify-center text-3xl"
              style={{ background: owner.color + "33" }}
            >
              {owner.emoji}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs uppercase tracking-wider font-bold text-[#556B7D]">Angler</div>
              <div className="font-display text-2xl font-black text-[#0A3D62]">{owner.name}</div>
              {loggedByOther && enterer && (
                <div className="text-sm text-[#556B7D] italic mt-0.5" data-testid="detail-logged-by">
                  Logged by {enterer.name}
                </div>
              )}
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div className="bg-white rounded-2xl p-4 border border-[#0A3D62]/5">
            <div className="text-xs uppercase tracking-wider font-bold text-[#556B7D]">Species</div>
            <div className="font-display text-xl font-black text-[#0A3D62] mt-1">
              {c.species || "Other"}
            </div>
          </div>
          <div className="bg-white rounded-2xl p-4 border border-[#0A3D62]/5">
            <div className="text-xs uppercase tracking-wider font-bold text-[#556B7D]">Logged</div>
            <div className="font-display text-base font-bold text-[#0A3D62] mt-1">
              {new Date(c.created_at).toLocaleString(undefined, {
                month: "short",
                day: "numeric",
                hour: "numeric",
                minute: "2-digit",
              })}
            </div>
          </div>
        </div>

        {/* Bulldust button */}
        <button
          onClick={() => setShowBulldust(true)}
          data-testid="detail-bulldust-btn"
          className={`w-full py-5 rounded-2xl font-black text-lg flex items-center justify-center gap-2 active:scale-[0.98] transition-transform ${
            iFlagged
              ? "bg-[#D9381E] text-white"
              : "bg-[#D9381E]/10 text-[#D9381E] border-2 border-[#D9381E]/20"
          }`}
        >
          <Flag className="w-5 h-5" strokeWidth={2.5} fill={iFlagged ? "white" : "none"} />
          {iFlagged ? "BULLDUST FLAGGED" : "BULLDUST 🚩"}
        </button>

        {flags.length > 0 && (
          <div className="text-center text-sm text-[#556B7D] font-semibold" data-testid="detail-flag-count">
            🚩 {flags.length} suspicious {flags.length === 1 ? "family member" : "family members"}
          </div>
        )}
      </div>

      {/* Bulldust confirm */}
      <AlertDialog open={showBulldust} onOpenChange={setShowBulldust}>
        <AlertDialogContent className="rounded-3xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl">
              {iFlagged ? "Unflag this catch?" : `Flag ${owner?.name || "this"}'s catch?`}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#556B7D]">
              {iFlagged
                ? "Removes your bulldust flag. All forgiven."
                : `Flag ${owner?.name || ""}'s ${c.length_cm.toFixed(0)}cm ${c.species || "fish"}? This does nothing except let the family know you're suspicious.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="detail-bulldust-cancel">Nah</AlertDialogCancel>
            <AlertDialogAction
              onClick={toggleBulldust}
              data-testid="detail-bulldust-confirm"
              className="bg-[#D9381E] hover:bg-[#D9381E]/90"
            >
              {iFlagged ? "Unflag" : "Flag it 🚩"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete confirm */}
      <AlertDialog open={showDelete} onOpenChange={setShowDelete}>
        <AlertDialogContent className="rounded-3xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl">Delete this catch?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#556B7D]">
              {owner?.name || "This angler"}'s {c.length_cm.toFixed(0)}cm gets wiped from history.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="detail-delete-cancel">Keep it</AlertDialogCancel>
            <AlertDialogAction
              onClick={doDelete}
              data-testid="detail-delete-confirm"
              className="bg-[#D9381E] hover:bg-[#D9381E]/90"
            >
              Wipe it
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit drawer */}
      <Drawer open={showEdit} onOpenChange={setShowEdit}>
        <DrawerContent className="bg-[#FAF8F3] max-h-[90vh]">
          <DrawerHeader>
            <DrawerTitle className="font-display text-2xl text-[#0A3D62]">Edit catch</DrawerTitle>
          </DrawerHeader>
          <div className="px-5 pb-6 space-y-5 overflow-y-auto">
            {/* Length */}
            <div>
              <div className="text-xs uppercase font-bold text-[#556B7D] mb-2">Length</div>
              <div className="flex items-center justify-between bg-white rounded-2xl p-3 border border-[#0A3D62]/10">
                <button
                  onClick={() => setEditLength((v) => Math.max(1, v - 1))}
                  className="w-14 h-14 rounded-2xl bg-[#0A3D62] text-white flex items-center justify-center active:scale-95"
                  data-testid="edit-length-minus"
                >
                  <Minus className="w-6 h-6" strokeWidth={3} />
                </button>
                <div className="font-display text-5xl font-black text-[#0A3D62]">
                  {editLength.toFixed(0)}
                  <span className="text-lg text-[#556B7D] ml-2">cm</span>
                </div>
                <button
                  onClick={() => setEditLength((v) => v + 1)}
                  className="w-14 h-14 rounded-2xl bg-[#E8A838] text-[#1A2A35] flex items-center justify-center active:scale-95"
                  data-testid="edit-length-plus"
                >
                  <PlusIcon className="w-6 h-6" strokeWidth={3} />
                </button>
              </div>
            </div>

            {/* Owner */}
            <div>
              <div className="text-xs uppercase font-bold text-[#556B7D] mb-2">Angler</div>
              <div className="flex gap-2 overflow-x-auto scrollbar-none pb-2">
                {members.map((m) => (
                  <button
                    key={m.id}
                    onClick={() => setEditOwner(m.id)}
                    data-testid={`edit-owner-${m.id}`}
                    className={`shrink-0 flex flex-col items-center gap-1 p-2 rounded-2xl transition ${
                      editOwner === m.id ? "bg-[#E8A838]" : "bg-white border border-[#0A3D62]/10"
                    }`}
                  >
                    <div
                      className="w-12 h-12 rounded-full flex items-center justify-center text-2xl"
                      style={{ background: m.color + "33" }}
                    >
                      {m.emoji}
                    </div>
                    <div className="text-xs font-bold text-[#0A3D62]">{m.name}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Species */}
            <div>
              <div className="text-xs uppercase font-bold text-[#556B7D] mb-2">Species</div>
              <div className="flex flex-wrap gap-2">
                {SPECIES.map((s) => (
                  <button
                    key={s}
                    onClick={() => setEditSpecies(s)}
                    className={`px-4 py-2 rounded-full font-bold text-sm active:scale-95 ${
                      editSpecies === s
                        ? "bg-[#0A3D62] text-white"
                        : "bg-white text-[#0A3D62] border border-[#0A3D62]/15"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <button
                onClick={() => setShowEdit(false)}
                className="flex-1 h-14 rounded-2xl bg-white border border-[#0A3D62]/10 text-[#0A3D62] font-bold flex items-center justify-center gap-2 active:scale-[0.98]"
                data-testid="edit-cancel"
              >
                <X className="w-5 h-5" strokeWidth={2.5} /> Cancel
              </button>
              <button
                onClick={saveEdit}
                className="flex-1 h-14 rounded-2xl bg-[#E8A838] text-[#1A2A35] font-black flex items-center justify-center gap-2 active:scale-[0.98]"
                data-testid="edit-save"
              >
                <Save className="w-5 h-5" strokeWidth={2.5} /> Save
              </button>
            </div>
          </div>
        </DrawerContent>
      </Drawer>
    </div>
  );
}
